/**
 * The §F5.2 picker's baseline clusters — client observation P5.
 *
 * The 2025 MELS baseline took the catalogue from 27 rows to 85, and 58 of the new ones are
 * *sub-indicators*: eight agroforestry practices, eleven soil practices, twenty crops, two
 * halves of one gendered extension question. They all carry `tier: 'other'`, so the picker
 * rendered them as one 58-row list under a single "Other indicators" heading, in which a
 * reader looking for mulching had to know it is called *Soil practice: mulching* first.
 *
 * ## What this does instead
 *
 * `<optgroup>` cannot nest — HTML has no second level — so the choice was between prefixed
 * labels inside one heading and **one optgroup per sheet**. This takes the second: the tiers
 * stay exactly as they are, and the baseline groups are appended as headings of their own.
 * Three reasons it wins:
 *
 * * a `<select>` is searchable by typing, and typing "mul" should land on mulching. Under a
 *   group heading the option can read **"Mulching"** rather than "Soil practice: mulching",
 *   so the reader types the word they are thinking of rather than the prefix somebody chose;
 * * the heading is where the sheet name belongs. Repeating "Soil practice: " eleven times
 *   inside one list spends eleven lines saying the same thing, in a 300 px sidebar that
 *   ellipsises the half that differs;
 * * it needs nothing new. `PaneOptGroup` already renders a heading and its options, so the
 *   change is which rows go under which label.
 *
 * ## The rules that keep it a list rather than a wall of headings
 *
 * * **a group of one is not a group.** `mb_conflict`, `mb_women_land` and `mb_income` are
 *   single series; each stays in the plain "Other" heading, under its full name, because a
 *   heading over one row is a heading for nothing. This also handles the domain rail for
 *   free: filter to a domain that holds two of a sheet's eleven rows and you get a cluster,
 *   filter to one that holds a single row and you get a row.
 * * **the leaf is the label, when there is one.** `name` is "Agroforestry: manure or compost
 *   applied to trees"; under the "Agroforestry" heading it reads "Manure or compost applied
 *   to trees". A name with no ": " in it is used whole — nothing is ever guessed at.
 * * **groups come after the tiers**, in catalogue order, because every baseline row is
 *   `tier: 'other'` and moving them above Contractual would reorder the picker for a reader
 *   who has never looked at the baseline.
 */
import type { Indicator } from '../../lib/api';

/**
 * The separator the baseline's `name` puts between the sheet and the sub-indicator: a colon,
 * which French sets after a narrow no-break space ("Rendement\u202f: maïs").
 */
const LEAF_SEPARATOR = /[\s\u202f]*:\s+/;

/**
 * The part of an indicator's name that is not its group — "Mulching" out of "Soil practice:
 * mulching", with the first letter restored to a capital.
 *
 * Splits on the **first** separator only: "Crop yield: chilli pepper" has one, and a leaf
 * that contained another colon would keep it. A name with no separator is returned unchanged,
 * which is the singleton case and also the safety net for a series named some other way.
 */
export function leafLabel(name: string): string {
  const match = LEAF_SEPARATOR.exec(name);
  if (!match) return name;
  const leaf = name.slice(match.index + match[0].length).trim();
  if (!leaf) return name;
  return leaf.charAt(0).toUpperCase() + leaf.slice(1);
}

export interface BaselineGroup {
  id: string;
  label: string;
  members: Indicator[];
}

/**
 * Partition rows into the ones that cluster and the ones that do not.
 *
 * `grouped` is every group with **two or more** members, in the order its first member
 * appears (which is `sort_order`, so it is the MEL team's own block order); `ungrouped` is
 * everything else, with the singleton baseline rows folded back in among the seed catalogue's
 * where they belong.
 */
export function partitionBaselineGroups(rows: Indicator[]): {
  grouped: BaselineGroup[];
  ungrouped: Indicator[];
} {
  const byGroup = new Map<string, BaselineGroup>();
  const loose: Indicator[] = [];
  for (const row of rows) {
    if (!row.group_id) {
      loose.push(row);
      continue;
    }
    const existing = byGroup.get(row.group_id);
    if (existing) existing.members.push(row);
    else
      byGroup.set(row.group_id, {
        id: row.group_id,
        label: row.group_label ?? row.group_id,
        members: [row],
      });
  }
  const grouped: BaselineGroup[] = [];
  for (const group of byGroup.values()) {
    if (group.members.length > 1) grouped.push(group);
    else loose.push(...group.members);
  }
  // `loose` is rebuilt out of two passes, so it has to be put back into catalogue order or a
  // singleton would jump to the end of its tier. The server's order is the row order it
  // arrived in, which is what this index restores.
  const order = new Map(rows.map((row, i) => [row.id, i]));
  loose.sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
  return { grouped, ungrouped: loose };
}

/**
 * The siblings of one indicator — every member of its group, itself included, in catalogue
 * order. Empty when it has no group or is the only member of one, which is the analytics
 * panel's "nothing to compare" case.
 */
export function siblingsOf(
  indicator: Indicator | null | undefined,
  rows: Indicator[],
): Indicator[] {
  if (!indicator?.group_id) return [];
  const family = rows.filter((row) => row.group_id === indicator.group_id);
  return family.length > 1 ? family : [];
}
